import { createContext, type PropsWithChildren, useContext } from 'react';

import { type ShareCustomColors } from '~/components/ShareModal/components/ShareDesignColorRow';
import { type CustomFontsInput } from '~/components/ShareModal/types/presentationSettingsInputs';
import {
  type CoverImageInput,
  type PermissionMode,
  type ShortUrlProps,
  type ShowcaseProps,
} from '~/components/ShowcaseModal/types';
import { type DownloadPermissionMode } from '~/utils/shortUrl/buildDownloadPermissionShortUrlUpdate';

export type ShowcaseModalContextValue = {
  // State
  showcaseProps: ShowcaseProps;
  shortUrlProps: ShortUrlProps;
  isSaving: boolean;
  isEditMode: boolean;
  errors: Record<string, string> | null;
  selectedItemCount?: number;
  hasCreatedShowcase?: boolean;

  // Update functions
  updateShowcaseName: (name: string) => void;
  updateShowcaseDescription: (description: string) => void;
  updateLinkAccessPermission: (value: boolean) => void;
  updateAssetPermission: <K extends keyof ShortUrlProps['permissions']['asset']>(
    key: K,
    value: ShortUrlProps['permissions']['asset'][K],
  ) => void;
  updateCommentPermissions: (mode: PermissionMode) => void;
  updateCustomFieldPermissions: (mode: PermissionMode) => void;
  updateDownloadPermissions: (mode: DownloadPermissionMode) => void;
  updateMetadataVisibility: (showMetadata: boolean) => void;
  updateHideSearchBar: (hideSearchBar: boolean) => void;
  updateHideAirLogo: (hideAirLogo: boolean) => void;
  updateCustomColors: (customColors: ShareCustomColors | undefined) => void;
  updateCoverImage: (coverImage: CoverImageInput | null) => void;
  updateCustomFonts: (customFonts: CustomFontsInput | null) => void;
  updateLockedControlsExpandNested: (expandNested: boolean) => void;
  updatePasscode: (passcode: string | null) => void;
  updateExpirationDate: (expirationDate: string | null) => void;

  // Save functions (optional in create mode)
  saveShowcaseName?: () => void;
  saveShowcaseDescription?: () => void;
  savePasscode?: (value?: string | null) => void;
  saveExpirationDate?: (value?: string | null) => void;

  // Actions
  onSave: () => void;
  onClose: () => void;
};

const ShowcaseModalContext = createContext<ShowcaseModalContextValue | null>(null);

export type ShowcaseModalProviderProps = PropsWithChildren<{
  value: ShowcaseModalContextValue;
}>;

export const ShowcaseModalProvider = ({ children, value }: ShowcaseModalProviderProps) => {
  return <ShowcaseModalContext.Provider value={value}>{children}</ShowcaseModalContext.Provider>;
};

export const useShowcaseModal = () => {
  const context = useContext(ShowcaseModalContext);

  if (!context) {
    throw new Error('useShowcaseModal must be used within ShowcaseModalProvider');
  }

  return context;
};
